import moment from "moment";
import Employee from "app/domain/Employee";
import { MonetaryAmount } from "app/domain/monetary-amount";
import { getLanguage } from "infrastructure/translate/localization";
import Datetime, {
  DateFormat,
} from "../../../v2/domain/common/entities/Datetime";

const amountConfig = (recurrentPayment) => {
  const { benefitDetails, fares } = recurrentPayment;

  return {
    "nursery-benefit": benefitDetails?.nurseryPrice,
    "education-benefit": benefitDetails?.totalTrainingAmount,
    "health-insurance-benefit": fares.totalAmount,
    "renting-benefit": fares.totalAmount,
    "wellness-benefit": fares.totalAmount,
    "retirement-insurance-benefit": fares.totalAmount,
    "pension-plan-benefit": fares.totalAmount,
  };
};

const insuranceConfig = (recurrentPayment) => {
  const {
    healthInsuranceModality,
    lifeInsuranceModality,
    lifeInsurancePolicy,
  } = recurrentPayment;

  return {
    "health-insurance-benefit": {
      modality: healthInsuranceModality?.modalityName,
      insuranceCompany: healthInsuranceModality?.insuranceCompany,
      brochureUrl: healthInsuranceModality?.modalityBrochures[0]?.url,
    },
    "life-insurance-benefit": {
      modality: lifeInsuranceModality?.name,
      insuranceCompany: lifeInsurancePolicy?.insuranceCompany,
      brochureUrl: lifeInsuranceModality?.brochures[0]?.url,
    },
  };
};

const amountDetailConfig = (recurrentPayment) => {
  const { benefitDetails, fares, lifeInsuranceCalculation } = recurrentPayment;
  const { nurseryPrice, totalTrainingAmount } = benefitDetails;
  const { totalAmount } = fares;

  return {
    "nursery-benefit": MonetaryAmount.ofCurrency({
      amountInCents: nurseryPrice?.amountInCents,
      currency: nurseryPrice?.currency,
    }),
    "education-benefit": MonetaryAmount.ofCurrency({
      amountInCents: totalTrainingAmount?.amountInCents,
      currency: totalTrainingAmount?.currency,
    }),
    "health-insurance-benefit": MonetaryAmount.ofCurrency({
      amountInCents: totalAmount?.amountInCents,
      currency: totalAmount?.currency,
    }),
    "life-insurance-benefit": MonetaryAmount.ofCurrency({
      amountInCents: lifeInsuranceCalculation?.totalCapital?.amountInCents,
      currency: lifeInsuranceCalculation?.totalCapital?.currency,
    }),
    "renting-benefit": MonetaryAmount.ofCurrency({
      amountInCents: totalAmount?.amountInCents,
      currency: totalAmount?.currency,
    }),
    "wellness-benefit": MonetaryAmount.ofCurrency({
      amountInCents: totalAmount?.amountInCents,
      currency: totalAmount?.currency,
    }),
  };
};

const companyAmountDetailConfig = (recurrentPayment) => {
  const { benefitDetails, fares } = recurrentPayment;
  const { totalTrainingAmountPaidByCompany, rentingCostPerPaymentCompany } =
    benefitDetails;
  const { allowance } = fares;
  const allowanceNotExempt = fares["allowance-not-exempt"];

  const monetaryAllowance = MonetaryAmount.ofCurrency({
    amountInCents: allowance.amountInCents,
    currency: allowance.currency,
  });
  const monetaryAllowanceNotExempt = MonetaryAmount.ofCurrency({
    amountInCents: allowanceNotExempt.amountInCents,
    currency: allowanceNotExempt.currency,
  });

  return {
    "education-benefit": totalTrainingAmountPaidByCompany
      ? MonetaryAmount.ofCurrency({
          amountInCents: totalTrainingAmountPaidByCompany?.amountInCents,
          currency: totalTrainingAmountPaidByCompany?.currency,
        })
      : null,
    "health-insurance-benefit": monetaryAllowance.add(
      monetaryAllowanceNotExempt
    ),
    "life-insurance-benefit": monetaryAllowance,
    "renting-benefit": rentingCostPerPaymentCompany
      ? MonetaryAmount.ofCurrency({
          amountInCents: rentingCostPerPaymentCompany?.amountInCents,
          currency: rentingCostPerPaymentCompany?.currency,
        })
      : MonetaryAmount.ZERO,
    "wellness-benefit": monetaryAllowance.add(monetaryAllowanceNotExempt),
  };
};

const employeeAmountDetailConfig = (recurrentPayment) => {
  const { benefitDetails, fares } = recurrentPayment;
  const {
    totalTrainingAmount,
    totalTrainingAmountPaidByCompany,
    rentingCostPerPaymentEmployee,
  } = benefitDetails;
  const { flex } = fares;
  const flexNotExempt = fares["flex-not-exempt"];

  const monetaryFlex = MonetaryAmount.ofCurrency({
    amountInCents: flex.amountInCents,
    currency: flex.currency,
  });
  const monetaryFlexNotExempt = MonetaryAmount.ofCurrency({
    amountInCents: flexNotExempt.amountInCents,
    currency: flexNotExempt.currency,
  });

  const monetaryTotalTraining = MonetaryAmount.ofCurrency({
    amountInCents: totalTrainingAmount?.amountInCents,
    currency: totalTrainingAmount?.currency,
  });

  const moneratyCompanyTraining = MonetaryAmount.ofCurrency({
    amountInCents: totalTrainingAmountPaidByCompany?.amountInCents,
    currency: totalTrainingAmountPaidByCompany?.currency,
  });

  return {
    "education-benefit": totalTrainingAmountPaidByCompany
      ? monetaryTotalTraining.subtract(moneratyCompanyTraining)
      : monetaryTotalTraining,
    "health-insurance-benefit": monetaryFlex.add(monetaryFlexNotExempt),
    "life-insurance-benefit": monetaryFlexNotExempt,
    "renting-benefit": rentingCostPerPaymentEmployee
      ? MonetaryAmount.ofCurrency({
          amountInCents: rentingCostPerPaymentEmployee?.amountInCents,
          currency: rentingCostPerPaymentEmployee?.currency,
        })
      : MonetaryAmount.ZERO,
    "wellness-benefit": monetaryFlex.add(monetaryFlexNotExempt),
  };
};

const productConfig = (recurrentPayment) => {
  const { wellnessProduct, retirementInsuranceProduct, pensionPlanProduct } =
    recurrentPayment;
  const currentLanguage = getLanguage();

  return {
    "wellness-benefit": wellnessProduct?.name,
    "retirement-insurance-benefit":
      retirementInsuranceProduct?.languages[currentLanguage]?.name ||
      retirementInsuranceProduct?.languages?.en?.name,
    "pension-plan-benefit":
      pensionPlanProduct?.languages[currentLanguage]?.name ||
      pensionPlanProduct?.languages?.en?.name,
  };
};

const StateConfig = {
  started: "confirm",
  pending: "pending",
  denied: "rejected",
  cancelled: "cancelled",
  finished: "finished",
};

const parseState = (state: string): string => StateConfig[state];

export const adaptRecurrentPayments = (recurrentPayment) => {
  const {
    category,
    employee,
    requestedAtTimestamp,
    state,
    approvals,
    benefitDetails,
  } = recurrentPayment;
  return {
    ...recurrentPayment,
    category,
    employee: new Employee({ ...employee }),
    requestDate: moment.unix(requestedAtTimestamp).toDate(),
    state: parseState(state),
    amount: amountConfig(recurrentPayment)[category],
    approvals,
    nursery: benefitDetails?.nurseryName,
    course: benefitDetails?.courseName,
    modality: insuranceConfig(recurrentPayment)[category]?.modality,
    insuranceCarrier:
      insuranceConfig(recurrentPayment)[category]?.insuranceCompany,
    brand: benefitDetails?.description,
    productName: productConfig(recurrentPayment)[category],
  };
};

const getTrainingInfo = (benefitDetails) => {
  if (!benefitDetails) return null;
  const { trainingStartDate, trainingEndDate } = benefitDetails;
  return {
    startDate: trainingStartDate
      ? new Datetime(trainingStartDate).toDatetimeString(
          DateFormat.SLASHED_DD_MMM_YYYY
        )
      : null,
    endDate: trainingEndDate
      ? new Datetime(trainingEndDate).toDatetimeString(
          DateFormat.SLASHED_DD_MMM_YYYY
        )
      : null,
  };
};

export const adaptRecurrentPaymentDetail = (recurrentPayment) => {
  const {
    category,
    employee,
    requestedAtTimestamp,
    state,
    approvals,
    benefitDetails,
    firstExecTimestamp,
    lastExecTimestamp,
    effectiveSince,
    healthInsuranceModality,
    healthInsuranceComplements,
    stateHistory,
    rentingRequestSteps,
    beneficiariesRecurrentPayments,
    wellnessProduct,
  } = recurrentPayment;

  function getBeneficiaries() {
    return beneficiariesRecurrentPayments.map(
      ({ recipient, state: recipientState, fares, effectiveDate }) => {
        const { totalAmount, allowance, flex } = fares;
        const allowanceNotExempt = fares["allowance-not-exempt"];
        const flexNotExempt = fares["flex-not-exempt"];

        const monetaryTotal = MonetaryAmount.ofCurrency({
          amountInCents: totalAmount.amountInCents,
          currency: totalAmount.currency,
        });

        const monetaryFlex = MonetaryAmount.ofCurrency({
          amountInCents: flex.amountInCents,
          currency: flex.currency,
        });
        const monetaryFlexNotExempt = MonetaryAmount.ofCurrency({
          amountInCents: flexNotExempt.amountInCents,
          currency: flexNotExempt.currency,
        });

        const monetaryAllowance = MonetaryAmount.ofCurrency({
          amountInCents: allowance.amountInCents,
          currency: allowance.currency,
        });
        const monetaryAllowanceNotExempt = MonetaryAmount.ofCurrency({
          amountInCents: allowanceNotExempt.amountInCents,
          currency: allowanceNotExempt.currency,
        });
        return {
          name: `${recipient.name} ${recipient.surname}`,
          state: parseState(recipientState),
          amount: monetaryTotal,
          companyAmount: monetaryAllowance.add(monetaryAllowanceNotExempt),
          employeeAmount: monetaryFlex.add(monetaryFlexNotExempt),
          date: effectiveDate ? moment(new Date(effectiveDate)).toDate() : null,
        };
      }
    );
  }

  return {
    ...recurrentPayment,
    category,
    employee: new Employee({ ...employee }),
    requestDate: new Date(requestedAtTimestamp * 1000),
    initialDate: firstExecTimestamp
      ? new Date(firstExecTimestamp * 1000)
      : null,
    finalDate: lastExecTimestamp ? new Date(lastExecTimestamp * 1000) : null,
    effectiveDate: effectiveSince
      ? moment(new Date(effectiveSince)).toDate()
      : null,
    state: parseState(state),
    amount: amountDetailConfig(recurrentPayment)[category],
    companyAmount: companyAmountDetailConfig(recurrentPayment)[category],
    employeeAmount: employeeAmountDetailConfig(recurrentPayment)[category],
    initialCompanyAmount: MonetaryAmount.ofCurrency({
      amountInCents: benefitDetails?.initialPaymentByCompany
        ? benefitDetails?.initialPaymentByCompany?.amountInCents
        : -1,
      currency: benefitDetails?.initialPaymentByCompany?.currency,
    }),
    initialEmployeeAmount: MonetaryAmount.ofCurrency({
      amountInCents: benefitDetails?.initialPaymentByEmployee
        ? benefitDetails?.initialPaymentByEmployee?.amountInCents
        : -1,
      currency: benefitDetails?.initialPaymentByEmployee?.currency,
    }),
    approvals,
    beneficiaries: getBeneficiaries(),
    payrollNumber: benefitDetails?.splitBy,
    nursery: benefitDetails?.nurseryName,
    course: benefitDetails?.courseName,
    centre: benefitDetails?.centreName,
    trainingInfo: getTrainingInfo(benefitDetails),
    modality: insuranceConfig(recurrentPayment)[category]?.modality,
    insuranceCarrier:
      insuranceConfig(recurrentPayment)[category]?.insuranceCompany,
    brand: benefitDetails?.description,
    healthModality: healthInsuranceModality
      ? {
          name: healthInsuranceModality.modalityName,
          modalityFee: MonetaryAmount.ofCurrency({
            amountInCents: healthInsuranceModality.modalityFee?.amountInCents,
            currency: healthInsuranceModality.currency?.currency,
          }),
        }
      : null,
    healthComplements: healthInsuranceComplements?.map((complement) => ({
      name: complement.complementName,
      complementFee: MonetaryAmount.ofCurrency({
        amountInCents: complement.complementFee?.amountInCents,
        currency: complement.complementFee?.currency,
      }),
    })),
    brochureUrl: insuranceConfig(recurrentPayment)[category]?.brochureUrl,
    rentingFiles: rentingRequestSteps
      ? rentingRequestSteps.map(({ step, answer, file }) => ({
          fileId: answer,
          fileName: file.filename,
          name: step.name,
        }))
      : [],
    rejectedReason:
      stateHistory[stateHistory.length - 1].metadata?.reason || "",
    productName: wellnessProduct?.name,
  };
};
